import * as THREE from 'three';

export class CameraControls {
    private camera: THREE.PerspectiveCamera;
    private domElement: HTMLElement;

    // Spherical coordinates
    private phi: number = Math.PI / 2; // Vertical angle (0 = up, PI = down)
    private theta: number = 0; // Horizontal angle

    // Limits
    private readonly minPhi = Math.PI * 0.25; // Don't look too far up
    private readonly maxPhi = Math.PI * 0.75; // Don't look too far down

    // Dynamics
    private targetPhi: number = Math.PI / 2;
    private targetTheta: number = 0;
    private damping = 0.1; // Default damping
    private inertia = 0.92; // How much velocity is retained

    // Velocity for inertia
    private velocityPhi = 0;
    private velocityTheta = 0;

    // Input
    private isPointerDown = false;
    private pointerStart = { x: 0, y: 0 };
    private lastPointerPos = { x: 0, y: 0 };

    // Sensitivities
    private readonly mouseSensitivity = 0.002;
    private readonly touchSensitivity = 0.005; // Higher for touch to feel responsive

    // Auto rotation
    private autoRotate = false;
    private autoRotateSpeed = 0.0005;
    private idleTime = 0;
    private readonly idleTimeout = 5000; // 5 seconds

    // Camera settings
    private readonly lookRadius = 0.1;

    constructor(camera: THREE.PerspectiveCamera, domElement: HTMLElement) {
        this.camera = camera;
        this.domElement = domElement;

        this.setupEventListeners();
        this.updateCamera();
    }

    private setupEventListeners(): void {
        this.domElement.addEventListener('pointerdown', this.onPointerDown.bind(this));
        window.addEventListener('pointermove', this.onPointerMove.bind(this));
        window.addEventListener('pointerup', this.onPointerUp.bind(this));
        window.addEventListener('pointercancel', this.onPointerUp.bind(this));

        // Prevent default touch actions (scrolling/zoom) on the canvas
        this.domElement.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });
        this.domElement.addEventListener('touchmove', (e) => e.preventDefault(), { passive: false });

        // Keyboard
        window.addEventListener('keydown', this.onKeyDown.bind(this));
    }

    private onPointerDown(event: PointerEvent): void {
        this.isPointerDown = true;
        this.pointerStart.x = event.clientX;
        this.pointerStart.y = event.clientY;
        this.lastPointerPos.x = event.clientX;
        this.lastPointerPos.y = event.clientY;

        // Stop any existing inertia or auto-rotation
        this.velocityPhi = 0;
        this.velocityTheta = 0;
        this.idleTime = 0;
        this.autoRotate = false;

        this.domElement.style.cursor = 'grabbing';
    }

    private onPointerMove(event: PointerEvent): void {
        if (!this.isPointerDown) return;

        // Calculate delta
        const deltaX = event.clientX - this.lastPointerPos.x;
        const deltaY = event.clientY - this.lastPointerPos.y;

        // Determine sensitivity based on input type
        const sensitivity = event.pointerType === 'touch' ? this.touchSensitivity : this.mouseSensitivity;

        // Update velocity (for inertia)
        this.velocityTheta = -deltaX * sensitivity;
        this.velocityPhi = deltaY * sensitivity;

        // Apply immediately for direct control
        this.targetTheta += this.velocityTheta;
        this.targetPhi += this.velocityPhi;

        // Clamp vertical angle
        this.targetPhi = Math.max(this.minPhi, Math.min(this.maxPhi, this.targetPhi));

        // Update tracking
        this.lastPointerPos.x = event.clientX;
        this.lastPointerPos.y = event.clientY;
        this.idleTime = 0;
    }

    private onPointerUp(): void {
        this.isPointerDown = false;
        this.domElement.style.cursor = 'grab';
    }

    private onKeyDown(event: KeyboardEvent): void {
        const rotateAmount = 0.1;
        switch (event.key) {
            case 'ArrowLeft': this.targetTheta += rotateAmount; break;
            case 'ArrowRight': this.targetTheta -= rotateAmount; break;
            case 'ArrowUp': this.targetPhi = Math.max(this.minPhi, this.targetPhi - rotateAmount); break;
            case 'ArrowDown': this.targetPhi = Math.min(this.maxPhi, this.targetPhi + rotateAmount); break;
        }
        this.idleTime = 0;
        this.autoRotate = false;
    }

    private updateCamera(): void {
        const lookX = Math.sin(this.phi) * Math.sin(this.theta);
        const lookY = Math.cos(this.phi);
        const lookZ = Math.sin(this.phi) * Math.cos(this.theta);

        this.camera.lookAt(
            this.camera.position.x + lookX * this.lookRadius,
            this.camera.position.y + lookY * this.lookRadius,
            this.camera.position.z + lookZ * this.lookRadius
        );
    }

    public update(deltaTime: number): void {
        // Auto rotation
        this.idleTime += deltaTime * 1000;
        if (this.idleTime > this.idleTimeout) {
            this.autoRotate = true;
        }

        if (this.autoRotate) {
            // Gentle constant rotation
            this.targetTheta += this.autoRotateSpeed * deltaTime * 1000;
        } else if (!this.isPointerDown) {
            // Apply Inertia when not holding
            this.targetTheta += this.velocityTheta;
            this.targetPhi += this.velocityPhi;

            // Decay velocity
            this.velocityTheta *= this.inertia;
            this.velocityPhi *= this.inertia;

            // Stop if too slow
            if (Math.abs(this.velocityTheta) < 0.0001) this.velocityTheta = 0;
            if (Math.abs(this.velocityPhi) < 0.0001) this.velocityPhi = 0;

            // Re-clamp in case inertia pushed it out
            this.targetPhi = Math.max(this.minPhi, Math.min(this.maxPhi, this.targetPhi));
        }

        // Smooth interpolation to target
        this.theta += (this.targetTheta - this.theta) * this.damping;
        this.phi += (this.targetPhi - this.phi) * this.damping;

        this.updateCamera();
    }

    public setPosition(phi: number, theta: number): void {
        this.phi = phi;
        this.theta = theta;
        this.targetPhi = phi;
        this.targetTheta = theta;
        this.velocityTheta = 0;
        this.velocityPhi = 0;
        this.updateCamera();
    }

    public dispose(): void {
        this.domElement.removeEventListener('pointerdown', this.onPointerDown.bind(this));
        window.removeEventListener('pointermove', this.onPointerMove.bind(this));
        window.removeEventListener('pointerup', this.onPointerUp.bind(this));
        window.removeEventListener('pointercancel', this.onPointerUp.bind(this));
        window.removeEventListener('keydown', this.onKeyDown.bind(this));
    }
}
